// A conexão com o servidor de tempo real, sem React: abre, assina a loja com o
// token, entrega cada evento a quem escuta, e reconecta com espera crescente
// (1 s, 2 s, 4 s… até 30 s) — o Cloud Run derruba conexões ao drenar uma
// instância e o teto de 60 min faz da reconexão rotina, não exceção. O
// `subscribe` vai de novo a cada reconexão; sem ele a loja não recebe nada.
export interface EventoDoServidor { type: string; [k: string]: unknown }
export type Situacao = 'conectando' | 'online' | 'reconectando' | 'parado';

export interface DependenciasDaConexao {
  url: string;
  tenantId: string;
  obterToken(): Promise<string>;
  aoEvento(evento: EventoDoServidor): void;
  aoMudar?(situacao: Situacao): void;
  criarSocket?(url: string): WebSocket;
  agendar?(fn: () => void, ms: number): unknown;
  cancelar?(id: unknown): void;
}

export const ESPERA_INICIAL_MS = 1000;
export const ESPERA_MAXIMA_MS = 30000;

export function esperaDaTentativa(tentativa: number): number {
  return Math.min(ESPERA_MAXIMA_MS, ESPERA_INICIAL_MS * 2 ** Math.max(0, tentativa - 1));
}

export function abrirConexao(deps: DependenciasDaConexao) {
  const criar = deps.criarSocket ?? ((u) => new WebSocket(u));
  const agendar = deps.agendar ?? ((fn, ms) => setTimeout(fn, ms));
  const cancelar = deps.cancelar ?? ((id) => clearTimeout(id as number));
  let socket: WebSocket | null = null;
  let tentativa = 0;
  let parado = false;
  let agendado: unknown = null;

  const mudar = (s: Situacao) => deps.aoMudar?.(s);

  function conectar() {
    if (parado) return;
    mudar(tentativa === 0 ? 'conectando' : 'reconectando');
    const ws = criar(deps.url);
    socket = ws;
    ws.onopen = async () => {
      tentativa = 0;
      const token = await deps.obterToken();
      if (ws !== socket) return;
      ws.send(JSON.stringify({ type: 'subscribe', tenantId: deps.tenantId, token, light: true }));
      mudar('online');
    };
    ws.onmessage = (e) => {
      let evento: EventoDoServidor | null = null;
      try { evento = JSON.parse(String(e.data)); } catch { return; }
      if (evento && typeof evento.type === 'string') deps.aoEvento(evento);
    };
    ws.onclose = () => {
      if (ws !== socket || parado) return;
      tentativa += 1;
      mudar('reconectando');
      agendado = agendar(conectar, esperaDaTentativa(tentativa));
    };
    ws.onerror = () => { /* o onclose vem em seguida e cuida da reconexão */ };
  }

  conectar();

  return {
    fechar() {
      parado = true;
      if (agendado !== null) cancelar(agendado);
      const ws = socket; socket = null;
      try { ws?.close(); } catch { /* já fechado */ }
      mudar('parado');
    },
  };
}

export function urlDoTempoReal(local: { protocol: string; host: string } = location): string {
  return `${local.protocol === 'https:' ? 'wss' : 'ws'}://${local.host}`;
}
